import { forwardRef } from 'react';

import { Field } from '@base-ui/react/field';
import { cn } from 'components/utils';

import {
  textInputHelperRecipe,
  textInputIconRecipe,
  textInputPillBoxRecipe,
  textInputPillControlRecipe,
  textInputPillRootRecipe,
} from './TextInput.recipe';

import type { TextInputPillProps } from './types';

/**
 * Design System 2.0 text input — the pill variant.
 *
 * Ported from Figma node 487:38319, which is a SEPARATE component set from the 60px input rather
 * than a variant of it, and differs in more than its radius:
 *
 *   - 42px tall against 60px. Both shapes now draw their value at the scale's default reading
 *     size; the pill was a step smaller until the type scale landed.
 *   - no floating label. The "Label" in the design sits on the value line and does not move, which
 *     makes it a placeholder. The accessible name therefore comes from a visually hidden
 *     `Field.Label` — a placeholder is not a name, and disappears the moment the user types.
 *   - helper text sits BELOW the border rather than inside it.
 *   - no Corner Radius axis, so no `position` and no grouping. Stacking pills flush would defeat
 *     the shape.
 *   - in dark mode it draws no border at all until focused or errored, separating from the page by
 *     fill alone.
 */
export const TextInputPill = forwardRef<HTMLInputElement, TextInputPillProps>(
  (
    { label, placeholder, leftIcon, rightIcon, helperText, error, disabled, className, rootClassName, ...rest },
    ref
  ) => (
    <Field.Root className={cn(textInputPillRootRecipe(), rootClassName)} disabled={disabled} invalid={error}>
      {/* Hidden rather than absent: `sr-only` keeps the control named for assistive technology
          while the design shows only the placeholder. */}
      <Field.Label className="sr-only">{label}</Field.Label>

      <div className={cn(textInputPillBoxRecipe(), className)} data-part="box">
        {leftIcon && <span className={textInputIconRecipe()}>{leftIcon}</span>}
        <Field.Control
          ref={ref}
          className={textInputPillControlRecipe()}
          placeholder={placeholder ?? label}
          {...rest}
        />
        {rightIcon && <span className={textInputIconRecipe()}>{rightIcon}</span>}
      </div>

      {helperText && error && (
        <Field.Error className={textInputHelperRecipe({ shape: 'pill' })} match>
          {helperText}
        </Field.Error>
      )}
      {helperText && !error && (
        <Field.Description className={textInputHelperRecipe({ shape: 'pill' })}>{helperText}</Field.Description>
      )}
    </Field.Root>
  )
);
